import { create } from 'zustand';
import type { Field } from '../types';
import type { Farm } from '../types/farm';
import { farmService } from '../services/farmService';
import { fieldService } from '../services/fieldService';
import { userService } from '../services/userService';
import { useAuthStore } from './authStore';
import { useGeographyStore } from './geographyStore';
import type { Region, SubRegion } from '../types/region';
import type { District } from '../types/field';
import { Role } from '../types/user';
import type { AreaManagerPermission } from '../types/user';
import {
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  ServerError,
} from '../types/errors';

/**
 * エラータイプ
 */
type ErrorType = 'auth' | 'permission' | 'network' | 'server' | 'general' | null;

interface FieldStore {
  fields: Field[];
  farms: Farm[];
  regions: Region[];
  subRegions: SubRegion[];
  areas: District[];
  displayedFields: Field[];
  selectedField: Field | null;
  hoveredFieldId: string | null;
  selectedFarmId: string | null;
  selectedFarmName: string | null;
  selectedDistrictName: string | null;
  loading: boolean;
  error: string | null;
  errorType: ErrorType;

  // Actions
  fetchFields: () => Promise<void>;
  selectField: (fieldId: string) => void;
  setHoveredField: (fieldId: string | null) => void;
  filterByDistrict: (districtId: string) => void;
  filterByFarm: (districtId: string, farmId: string) => void;
  loadFieldsForFarm: (farmId: number) => Promise<void>;
  updateField: (
    fieldId: string,
    payload: { area?: number; predictedYield?: number | null }
  ) => Promise<Field>;
  clearError: () => void;
  reset: () => void;
}

/**
 * 圃場データを管理するZustandストア
 *
 * ロールに応じたデータ取得と、地区/農場によるフィルタリングを提供する。
 */
export const useFieldStore = create<FieldStore>((set, get) => ({
  fields: [],
  farms: [],
  regions: [],
  subRegions: [],
  areas: [],
  displayedFields: [],
  selectedField: null,
  hoveredFieldId: null,
  selectedFarmId: null,
  selectedFarmName: null,
  selectedDistrictName: null,
  loading: false,
  error: null,
  errorType: null,

  fetchFields: async () => {
    set({ loading: true, error: null, errorType: null });
    try {
      const user = useAuthStore.getState().user;
      if (!user) {
        set({ loading: false });
        return;
      }

      let farms: Farm[] = [];
      let fields: Field[] = [];
      let regions: Region[] = [];
      let subRegions: SubRegion[] = [];

      if (user.role === Role.FARMER) {
        // FARMER: 自分の農場一覧を取得し、全農場の圃場を読み込む
        farms = await userService.getMyFarms();
        const fieldArrays = await Promise.all(
          farms.map((farm) => farmService.getFieldsByFarm(farm.id))
        );
        fields = fieldArrays.flat();
      } else if (user.role === Role.AREA_MANAGER) {
        // AREA_MANAGER: 権限のある地区の農場を取得
        const perm = user.permission as AreaManagerPermission;
        const areaIds = perm.areas?.map((a) => a.id) ?? [];
        const farmArrays = await Promise.all(
          areaIds.map((areaId) => farmService.getFarmsByArea(areaId))
        );
        farms = farmArrays.flat();
        const fieldArrays = await Promise.all(
          farms.map((farm) => farmService.getFieldsByFarm(farm.id))
        );
        fields = fieldArrays.flat();
      } else if (user.role === Role.ADMIN) {
        // ADMIN: 地理マスタツリー（構成情報）を geographyStore から取得し、
        // LeftPanel の Region > SubRegion > Area ナビ用にフラットな regions/subRegions を導出する。
        const geography = useGeographyStore.getState();
        await geography.fetchTree();
        // geographyStore は取得失敗を握り潰してエラー state に積むため、ここで明示的に検知して
        // throw し、下の catch（エラー表示）に載せる。握り潰したままだと regions/subRegions が
        // 空のまま続行し、LeftPanel の階層ナビが無言で消える回帰になる。
        const geographyError = useGeographyStore.getState().error;
        if (geographyError) {
          throw new Error(geographyError);
        }
        const tree = useGeographyStore.getState().tree;
        regions = tree.map((r) => ({ id: r.id, name: r.name }));
        subRegions = tree.flatMap((r) =>
          r.subRegions.map((sr) => ({ id: sr.id, name: sr.name, regionId: r.id }))
        );
        farms = await userService.getMyFarms();
        const fieldArrays = await Promise.all(
          farms.map((farm) => farmService.getFieldsByFarm(farm.id))
        );
        fields = fieldArrays.flat();
        // fields の district に subRegionId をマージ（ツリー索引から解決）
        const resolveByAreaId = useGeographyStore.getState().resolveByAreaId;
        fields = fields.map((f) => {
          const srId = resolveByAreaId(f.district.id)?.subRegion.id;
          if (srId && !f.district.subRegionId) {
            return { ...f, district: { ...f.district, subRegionId: srId } };
          }
          return f;
        });
      }

      // fields から一意な地区リストを導出
      const areaMap = new Map<string, District>();
      for (const f of fields) {
        if (!areaMap.has(f.district.id)) {
          areaMap.set(f.district.id, f.district);
        }
      }
      const areas: District[] = [...areaMap.values()];

      const displayedFields = fields.filter((f) => f.name !== undefined);

      // リスク・作業可否の取得は FieldListPage / FieldDetailPage 側で
      // TanStack Query フック（useWeatherRisksForMultipleFields など）が担うため、
      // fieldStore からの prefetch は削除した。

      set({
        fields,
        farms,
        regions,
        subRegions,
        areas,
        displayedFields,
        loading: false,
        selectedFarmId: null,
        selectedFarmName: null,
        selectedDistrictName: null,
      });
    } catch (error) {
      let errorMessage: string;
      let errorType: ErrorType = 'general';

      if (error instanceof AuthenticationError) {
        errorMessage = error.message;
        errorType = 'auth';
      } else if (error instanceof AuthorizationError) {
        errorMessage = error.message;
        errorType = 'permission';
      } else if (error instanceof NetworkError) {
        errorMessage = error.message;
        errorType = 'network';
      } else if (error instanceof ServerError) {
        errorMessage = error.message;
        errorType = 'server';
      } else {
        errorMessage = error instanceof Error ? error.message : '圃場データの取得に失敗しました';
      }

      set({ error: errorMessage, errorType, loading: false });
    }
  },

  selectField: (fieldId: string) => {
    const { fields } = get();
    const field = fields.find((f) => f.id === fieldId) || null;
    set({
      selectedField: field,
      selectedDistrictName: null,
      selectedFarmId: null,
      selectedFarmName: null,
    });
  },

  setHoveredField: (fieldId: string | null) => {
    set({ hoveredFieldId: fieldId });
  },

  filterByDistrict: (districtId: string) => {
    const { fields } = get();
    const displayedFields = fields.filter(
      (f) => f.district.id === districtId && f.name !== undefined
    );
    const districtName = displayedFields.length > 0 ? displayedFields[0].district.name : null;
    set({
      displayedFields,
      selectedDistrictName: districtName,
      selectedFarmId: null,
      selectedFarmName: null,
    });
  },

  filterByFarm: (districtId: string, farmId: string) => {
    const { fields } = get();
    const displayedFields = fields.filter(
      (f) =>
        (!districtId || f.district.id === districtId) && f.farmId === farmId && f.name !== undefined
    );
    const farmName = displayedFields.length > 0 ? displayedFields[0].farmName : null;
    const districtName = displayedFields.length > 0 ? displayedFields[0].district.name : null;
    set({
      displayedFields,
      selectedFarmId: farmId,
      selectedFarmName: farmName ?? null,
      selectedDistrictName: districtName,
    });
  },

  loadFieldsForFarm: async (farmId: number) => {
    set({ loading: true, error: null });
    try {
      const newFields = await farmService.getFieldsByFarm(farmId);
      const { fields } = get();
      const updatedFields = [...fields.filter((f) => f.farmId !== String(farmId)), ...newFields];
      set({
        fields: updatedFields,
        displayedFields: newFields.filter((f) => f.name !== undefined),
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '圃場データの取得に失敗しました',
        errorType: 'general',
        loading: false,
      });
    }
  },

  updateField: async (
    fieldId: string,
    payload: { area?: number; predictedYield?: number | null }
  ) => {
    const updated = await fieldService.updateField(fieldId, payload);
    const { fields, displayedFields, selectedField } = get();
    const replace = (f: Field) => (f.id === updated.id ? updated : f);
    set({
      fields: fields.map(replace),
      displayedFields: displayedFields.map(replace),
      selectedField: selectedField?.id === updated.id ? updated : selectedField,
    });
    return updated;
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      fields: [],
      farms: [],
      regions: [],
      subRegions: [],
      areas: [],
      displayedFields: [],
      selectedField: null,
      hoveredFieldId: null,
      selectedFarmId: null,
      selectedFarmName: null,
      selectedDistrictName: null,
      loading: false,
      error: null,
      errorType: null,
    });
  },
}));
